import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Zap, GitBranch, ShieldCheck, Bell, Hourglass, Workflow, ChevronLeft, ChevronRight,
  CheckCircle2, ArrowRight, Sparkles,
} from "lucide-react";

/**
 * In-app tutorial that explains every Flow Builder concept (Trigger, Condition,
 * Notify, Action, Wait, Branch). Triggered from a "?" button in the canvas
 * toolbar and on the flow list page. Walks the user through 7 mini-slides.
 */
const SLIDES = [
  {
    icon: Sparkles, color: "#8B1A1A",
    title: "Welcome to Flow Builder",
    body: (
      <>
        <p className="text-sm">
          Flow Builder lets you build <b>automations</b> visually — no code. Think of it as a recipe:
          <i>"when X happens, then do Y, and maybe Z if some condition is true."</i>
        </p>
        <ul className="mt-3 text-xs space-y-1 list-disc list-inside text-slate-600 dark:text-slate-300">
          <li>Drag nodes onto the canvas to define your steps.</li>
          <li>Connect them with arrows to set the flow order.</li>
          <li>Save → Activate → Test from Insights tab.</li>
        </ul>
      </>
    ),
  },
  {
    icon: Zap, color: "#22C55E", node: "TRIGGER",
    title: "1. Trigger — the starting event",
    body: (
      <>
        <p className="text-sm">Every flow starts with a <b>Trigger</b>. It's the event that wakes the flow up.</p>
        <div className="mt-3 p-3 rounded bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-xs space-y-1.5">
          <div><b>Examples</b></div>
          <div>• "When a new order is placed" (ORDER_PLACED)</div>
          <div>• "Every day at 9 AM" (DAILY_AT_TIME)</div>
          <div>• "When a salesman is late to check in" (LATE_CHECKIN)</div>
        </div>
        <p className="mt-2 text-xs text-slate-500">There can only be one trigger per flow — it's the starting line.</p>
      </>
    ),
  },
  {
    icon: ShieldCheck, color: "#0EA5E9", node: "CONDITION",
    title: "2. Condition — a yes/no check",
    body: (
      <>
        <p className="text-sm">A <b>Condition</b> checks a value and branches into YES or NO.</p>
        <div className="mt-3 p-3 rounded bg-sky-50 dark:bg-sky-900/30 border border-sky-200 dark:border-sky-800 text-xs space-y-1.5">
          <div><b>Example</b>: "Is the order amount over ₹50,000?"</div>
          <div className="flex items-center gap-2 mt-2">
            <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">YES</span>
            <ArrowRight size={12} />
            <span>Notify Sales Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-700 font-semibold">NO</span>
            <ArrowRight size={12} />
            <span>Continue silently</span>
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-500">Use operators: EQ (=), GT (&gt;), LT (&lt;), CONTAINS, IS_NULL, etc.</p>
      </>
    ),
  },
  {
    icon: Bell, color: "#8B5CF6", node: "NOTIFY",
    title: "3. Notify — send a message",
    body: (
      <>
        <p className="text-sm">A <b>Notify</b> step sends a notification to one or more people.</p>
        <div className="mt-3 p-3 rounded bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-800 text-xs space-y-1.5">
          <div><b>Channels</b>: in-app, push (mobile), email, SMS, WhatsApp.</div>
          <div><b>Recipients</b>: a specific user, a whole role (e.g. SUPER_ADMIN), or a region.</div>
          <div className="mt-2 pt-2 border-t border-violet-200 dark:border-violet-700">
            <div><b>Title</b>: <code className="bg-white dark:bg-slate-900 px-1 rounded">{`{{dealer_name}} placed a big order!`}</code></div>
            <div><b>Body</b>: <code className="bg-white dark:bg-slate-900 px-1 rounded">{`₹{{amount}} via {{salesman_name}}`}</code></div>
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          Anything in <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{`{{double curlies}}`}</code> is replaced with real data from the trigger.
        </p>
      </>
    ),
  },
  {
    icon: Workflow, color: "#F59E0B", node: "ACTION",
    title: "4. Action — perform a task",
    body: (
      <>
        <p className="text-sm">An <b>Action</b> does something in the system, not just notify.</p>
        <div className="mt-3 p-3 rounded bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-xs space-y-1.5">
          <div><b>Examples</b></div>
          <div>• Create a follow-up task for the manager.</div>
          <div>• Auto-approve small expenses below ₹500.</div>
          <div>• Tag a dealer as "HIGH_RISK".</div>
        </div>
        <p className="mt-2 text-xs text-slate-500">Configure the action type + parameters in the node editor.</p>
      </>
    ),
  },
  {
    icon: Hourglass, color: "#64748B", node: "WAIT",
    title: "5. Wait — pause before next step",
    body: (
      <>
        <p className="text-sm">A <b>Wait</b> step pauses the flow before doing the next thing.</p>
        <div className="mt-3 p-3 rounded bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-xs space-y-1.5">
          <div><b>Example</b>: After approving an order, wait 30 minutes, then send a "Did you receive the dispatch details?" follow-up.</div>
        </div>
        <p className="mt-2 text-xs text-slate-500">Useful for nudges, reminders, and time-sensitive escalations.</p>
      </>
    ),
  },
  {
    icon: GitBranch, color: "#EC4899", node: "BRANCH",
    title: "6. Branch — multiple paths",
    body: (
      <>
        <p className="text-sm">A <b>Branch</b> is like a Condition but with more than 2 paths.</p>
        <div className="mt-3 p-3 rounded bg-pink-50 dark:bg-pink-900/30 border border-pink-200 dark:border-pink-800 text-xs space-y-1.5">
          <div><b>Example</b>: route an order by amount</div>
          <div>• <span className="px-1.5 rounded bg-pink-200 dark:bg-pink-800">small</span> &lt; ₹10k → auto-approve</div>
          <div>• <span className="px-1.5 rounded bg-pink-200 dark:bg-pink-800">medium</span> ₹10k–₹50k → notify regional sales</div>
          <div>• <span className="px-1.5 rounded bg-pink-200 dark:bg-pink-800">large</span> &gt; ₹50k → escalate to super admin</div>
          <div>• <span className="px-1.5 rounded bg-pink-200 dark:bg-pink-800">else</span> → catch-all fallback</div>
        </div>
        <p className="mt-2 text-xs text-slate-500">Each branch has a label — connect arrows from the branch node to the next step for each label.</p>
      </>
    ),
  },
  {
    icon: CheckCircle2, color: "#22C55E",
    title: "You're ready to build! 🚀",
    body: (
      <>
        <p className="text-sm">A few last tips:</p>
        <ul className="mt-2 text-xs space-y-1.5 list-disc list-inside text-slate-600 dark:text-slate-300">
          <li>Pause noisy default flows from the Active/Paused pill on each card.</li>
          <li>Test your flow with the <b>Emit test event</b> button on the Insights tab.</li>
          <li>Browse the <b>Marketplace</b> tab for ready-made flows you can install in one click.</li>
          <li>Define your own custom triggers via <b>New trigger</b> in the flow wizard.</li>
        </ul>
      </>
    ),
  },
];

export default function FlowTutorialDialog({ open, onClose }) {
  const [idx, setIdx] = useState(0);
  const slide = SLIDES[idx];
  const Icon = slide.icon;
  const last = idx === SLIDES.length - 1;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); setIdx(0); } }}>
      <DialogContent className="max-w-2xl" data-testid="flow-tutorial-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: slide.color + "22", color: slide.color }}>
              <Icon size={18} />
            </span>
            {slide.title}
            {slide.node && <Badge variant="outline" className="ml-1 text-[10px] uppercase tracking-wider" style={{ borderColor: slide.color, color: slide.color }}>{slide.node}</Badge>}
          </DialogTitle>
        </DialogHeader>
        <div className="py-2 text-slate-700 dark:text-slate-200 min-h-[200px]">
          {slide.body}
        </div>
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex gap-1">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#8B1A1A]" : "w-1.5 bg-slate-300 dark:bg-slate-600"}`}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`tutorial-dot-${i}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={idx === 0}
              className="bg-white dark:bg-slate-700 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 font-medium"
              data-testid="tutorial-prev-btn"
            >
              <ChevronLeft size={14} className="mr-1" /> Back
            </Button>
            {last ? (
              <Button onClick={() => { onClose(); setIdx(0); }} className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white" data-testid="tutorial-finish-btn">
                Got it!
              </Button>
            ) : (
              <Button onClick={() => setIdx((i) => Math.min(SLIDES.length - 1, i + 1))} className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white" data-testid="tutorial-next-btn">
                Next <ChevronRight size={14} className="ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
